import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Stack,
    IconButton,
    Collapse,
    Paper,
    Chip,
    Popover,
    TextField,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    Button
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { SearchHistory } from '../../../models/SearchHistory';
import { useDebounce } from '../../../hooks/useDebounce';
import { getSearchHistory } from '../../../services/historyService';
import { getLeadsByBusinessIds } from '../../../services/leadService';
import { StyledChip } from '../../../components/shared/StyledTable';
import { SharedTable, Column } from '../../../components/shared/table/Table';
import FilterListIcon from '@mui/icons-material/FilterList';

type Order = 'asc' | 'desc';
type OrderBy = 'createdAt' | 'location' | 'resultsCount';

interface SearchHistoryRow extends SearchHistory {
    isExpanded: boolean;
    metrics: {
        analyzed: number;
        leads: number;
        conversions: number;
    };
}

const SearchHistoryTable: React.FC = () => {
    const navigate = useNavigate();
    const [searchHistories, setSearchHistories] = useState<SearchHistoryRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<OrderBy>('createdAt');
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [locationFilterAnchor, setLocationFilterAnchor] = useState<null | HTMLElement>(null);
    const [locationSearchQuery, setLocationSearchQuery] = useState('');

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    // Get unique locations from all search histories
    const availableLocations = useMemo(() => {
        const locations = new Set<string>();
        searchHistories.forEach(history => {
            history.filters?.location?.forEach(loc => locations.add(loc));
        });
        return Array.from(locations).sort();
    }, [searchHistories]);

    // Filter locations based on search
    const filteredLocations = useMemo(() => {
        if (!locationSearchQuery) return availableLocations;
        return availableLocations.filter(loc =>
            loc.toLowerCase().includes(locationSearchQuery.toLowerCase())
        );
    }, [availableLocations, locationSearchQuery]);

    const handleLocationFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setLocationFilterAnchor(event.currentTarget);
    };

    const handleLocationFilterClose = () => {
        setLocationFilterAnchor(null);
    };

    const handleLocationToggle = (location: string) => {
        setSelectedLocations(prev =>
            prev.includes(location)
                ? prev.filter(l => l !== location)
                : [...prev, location]
        );
    };

    const clearLocationFilters = () => {
        setSelectedLocations([]);
    };

    const handleRequestSort = (property: OrderBy) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const toggleRowExpansion = (id: string) => {
        setSearchHistories(prev =>
            prev.map(history =>
                history.id === id
                    ? { ...history, isExpanded: !history.isExpanded }
                    : history
            )
        );
    };

    const loadSearchHistory = async () => {
        try {
            setLoading(true);
            setError(null);
            const historyData = await getSearchHistory();

            const allBusinessIds = historyData.flatMap(history =>
                history.results.map(result => result.id)
            );
            const leadMap = await getLeadsByBusinessIds(allBusinessIds);

            const historyWithMetrics: SearchHistoryRow[] = historyData.map(history => {
                const results = history.results || [];
                return {
                    ...history,
                    isExpanded: false,
                    metrics: {
                        analyzed: results.length,
                        leads: results.filter(r => leadMap.get(r.id)).length,
                        conversions: 0
                    }
                };
            });
            setSearchHistories(historyWithMetrics);
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An error occurred while loading search history');
            console.error('Error loading search history:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSearchHistory();
    }, []);

    const sortedAndFilteredHistory = useMemo(() => {
        let filtered = searchHistories;

        // Apply location filter
        if (selectedLocations.length > 0) {
            filtered = filtered.filter(history =>
                history.filters?.location?.some(loc =>
                    selectedLocations.includes(loc)
                )
            );
        }

        // Apply search filter
        if (debouncedSearchQuery) {
            filtered = filtered.filter(history =>
                history.filters?.industries?.some(industry =>
                    industry.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
                ) ||
                history.filters?.location?.some(loc =>
                    loc.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
                )
            );
        }

        return filtered.sort((a, b) => {
            if (orderBy === 'createdAt') {
                return (order === 'asc' ?
                    a.createdAt.getTime() - b.createdAt.getTime() :
                    b.createdAt.getTime() - a.createdAt.getTime()
                );
            } else if (orderBy === 'location') {
                const locA = a.filters?.location?.[0] || '';
                const locB = b.filters?.location?.[0] || '';
                return (order === 'asc' ?
                    locA.localeCompare(locB) :
                    locB.localeCompare(locA)
                );
            } else {
                const aCount = a.resultsCount || 0;
                const bCount = b.resultsCount || 0;
                return (order === 'asc' ? aCount - bCount : bCount - aCount);
            }
        });
    }, [searchHistories, debouncedSearchQuery, order, orderBy, selectedLocations]);

    const columns: Column<SearchHistoryRow>[] = [
        {
            id: 'createdAt',
            label: 'Date',
            width: 180,
            sortable: true,
            render: (row) => row.createdAt.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        },
        {
            id: 'location',
            label: 'Location',
            width: 200,
            sortable: true,
            render: (row) => (
                <Box>
                    {row.filters?.location?.map((loc, index) => (
                        <StyledChip
                            key={index}
                            label={loc}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                        />
                    ))}
                </Box>
            )
        },
        {
            id: 'industries',
            label: 'Industries',
            width: 250,
            render: (row) => (
                <Box>
                    {row.filters?.industries?.map((industry, index) => (
                        <StyledChip
                            key={index}
                            label={industry}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                        />
                    ))}
                </Box>
            )
        },
        {
            id: 'resultsCount',
            label: 'Results',
            width: 100,
            sortable: true,
            render: (row) => row.resultsCount || 0
        },
        {
            id: 'metrics',
            label: 'Metrics',
            width: 300,
            render: (row) => (
                <Stack direction="row" spacing={1}>
                    <StyledChip
                        label={`${row.metrics.analyzed} Analyzed`}
                        size="small"
                    />
                    <StyledChip
                        label={`${row.metrics.leads} Leads`}
                        size="small"
                    />
                    <StyledChip
                        label={`${row.metrics.conversions} Converted`}
                        size="small"
                    />
                </Stack>
            )
        }
    ];

    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={handleLocationFilterClick}
                    color={selectedLocations.length > 0 ? "primary" : "inherit"}
                >
                    Locations ({selectedLocations.length})
                </Button>
            </Stack>

            <Popover
                open={Boolean(locationFilterAnchor)}
                anchorEl={locationFilterAnchor}
                onClose={handleLocationFilterClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 2, width: 300 }}>
                    <Stack spacing={2}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search locations..."
                            value={locationSearchQuery}
                            onChange={(e) => setLocationSearchQuery(e.target.value)}
                        />
                        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                            {filteredLocations.map((location) => (
                                <ListItem
                                    key={location}
                                    dense
                                    button
                                    onClick={() => handleLocationToggle(location)}
                                >
                                    <Checkbox
                                        edge="start"
                                        checked={selectedLocations.includes(location)}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                    <ListItemText primary={location} />
                                </ListItem>
                            ))}
                        </List>
                        {selectedLocations.length > 0 && (
                            <Button
                                variant="text"
                                onClick={clearLocationFilters}
                                sx={{ alignSelf: 'flex-end' }}
                            >
                                Clear All
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Popover>

            <SharedTable
                columns={columns}
                data={sortedAndFilteredHistory}
                loading={loading}
                error={error}
                orderBy={orderBy}
                order={order}
                searchQuery={searchQuery}
                searchPlaceholder="Search by industry or location..."
                onSearchChange={setSearchQuery}
                onRequestSort={handleRequestSort}
                onRowClick={(row) => navigate(`/history/${row.id}`)}
                getRowId={(row) => row.id}
                emptyStateMessage="No search history found"
            />
        </Box>
    );
};

export default SearchHistoryTable; 